import { Insurance as InsuranceType } from '@core/constants/data-types.constants';
import { formatISO, parseISO } from 'date-fns';
import { Config } from '../config.model';
import { ConcreteInsuranceIds, LifePlanInsurances } from '../insurance.model';
import {
  FinancialData,
  getCurrentAge,
  InsurancesData,
  LifePlan,
  PersonalData,
  ProfessionalData
} from '../life-plan.model';
import { ChildsEducationPrediction } from './childs-education-prediction.model';
import { DeathProtectionPrediction } from './death-prediction.model';
import { Debt } from './debt.model';
import { EducationStudy } from './education-study.model';
import { Employment, getEmploymentFromProfessionalData } from './employment.model';
import { FinancialAdvice } from './financial-advice.model';
import { FinancialPrediction } from './financial-prediction.model';
import { FinancialStatement } from './financial-statement.model';
import { FutureGoal } from './future-goal.model';
import { FutureGoalsPrediction } from './future-goals-prediction.model';
import { Insurance } from './insurance.model';
import { Investment } from './investment.model';
import { generateOfferingProductFromProduct, OfferingProduct } from './offering-product.model';
import { Person } from './person.model';
import { Rating } from './rating.model';
import { RetirementParams } from './retirement-params.model';
import { RetirementPrediction } from './retirement-prediction.model';
import { Salary } from './salary.model';

export const USER_IDENTIFIER = 'self';
export const SPOUSE_IDENTIFIER = 'partner';
export const FINANCIAL_STATEMENT_IDENTIFIER_PREFIX = 'financialStatement-';
export const INVESTMENT_IDENTIFIER_PREFIX = 'identifier-';
export const FUTURE_GOALS_PURPOSE_SAFETY_NET_SUFFIX = 'safetyNet';
export const FUTURE_GOALS_PURPOSE_INVESTMENTS_SUFFIX = 'investments';

export interface ReportsRequest {
  currency?: string;
  childs?: Person[];
  childsEducationStudies?: EducationStudy[];
  childsEducationSelectedProducts?: OfferingProduct[];
  deathProtectionSelectedProducts?: OfferingProduct[];
  debts?: Debt[];
  dependentPeople?: Person[];
  employment?: Employment;
  financialStatements?: FinancialStatement[];
  futureGoals?: FutureGoal[];
  futureGoalsSelectedProducts?: OfferingProduct[];
  insurances?: Insurance[];
  investments?: Investment[];
  investorProfile?: string;
  partner?: Person;
  partnerDeathProtectionSelectedProducts?: OfferingProduct[];
  partnerEmployment?: Employment;
  partnerInvestments?: Investment[];
  partnerRetirementSelectedProducts?: OfferingProduct[];
  partnerSalaries?: Salary[];
  retirementParams?: RetirementParams;
  retirementSelectedProducts?: OfferingProduct[];
  salaries?: Salary[];
  self?: Person;
}

export interface ReportsResponse {
  childsEducationOfferingProducts: OfferingProduct[];
  childsEducationPredictions: ChildsEducationPrediction[];
  childsEducationPredictionsWithoutProducts: ChildsEducationPrediction[];
  deathProtectionOfferingProducts: OfferingProduct[];
  deathProtectionPrediction: DeathProtectionPrediction;
  deathProtectionPredictionWithoutProducts: DeathProtectionPrediction;
  financialPrediction: FinancialPrediction;
  futureGoalsOfferingProducts: OfferingProduct[];
  futureGoalsPredictions: FutureGoalsPrediction[];
  futureGoalsPredictionsWithoutProducts: FutureGoalsPrediction[];
  globalRating: Rating;
  globalRatingWithoutProducts: Rating;
  partnerDeathProtectionOfferingProducts?: OfferingProduct[];
  partnerDeathProtectionPrediction?: DeathProtectionPrediction;
  partnerDeathProtectionPredictionWithoutProducts?: DeathProtectionPrediction;
  partnerRetirementOfferingProducts?: OfferingProduct[];
  partnerRetirementPrediction?: RetirementPrediction;
  partnerRetirementPredictionWithoutProducts?: RetirementPrediction;
  retirementOfferingProducts: OfferingProduct[];
  retirementPrediction: RetirementPrediction;
  retirementPredictionWithoutProducts: RetirementPrediction;
  financialAdvices: FinancialAdvice[];
  errorMessage?: any;
  _metadata?: any;
}

export function generateReportsRequest(lifePlan: LifePlan, config: Config): ReportsRequest {
  const reportsRequest: ReportsRequest = generateEmptyReportsRequest();
  reportsRequest.currency = config.defaultCurrency;
  reportsRequest.retirementParams = config.sourceConfigurations?.defaultRetirementParams;
  // User
  const userPersonalData = lifePlan.user?.personal;
  if (userPersonalData) {
    updateReportsRequestWithUserPersonalData(reportsRequest, userPersonalData);
  }
  const userProfessionalData = lifePlan.user?.professional;
  if (userProfessionalData) {
    updateReportsRequestWithUserProfessionalData(reportsRequest, userProfessionalData);
  }
  const userFinancialData = lifePlan.user?.financial;
  if (userFinancialData && lifePlan.insurances) {
    updateReportsRequestWithUserFinancialData(reportsRequest, userFinancialData, lifePlan.insurances, config);
  }
  // Spouse
  const spousePersonalData = lifePlan.spouse?.personal;
  if (spousePersonalData) {
    updateReportsRequestWithSpousePersonalData(reportsRequest, spousePersonalData);
  }
  const spouseProfessionalData = lifePlan.spouse?.professional;
  if (spouseProfessionalData) {
    updateReportsRequestWithSpouseProfessionalData(reportsRequest, spouseProfessionalData);
  }
  const spouseFinancialData = lifePlan.spouse?.financial;
  if (spouseFinancialData) {
    updateReportsRequestWithSpouseFinancialData(reportsRequest, spouseFinancialData);
  }
  // Both (insurances)
  updateReportsRequestWithInsurancesData(reportsRequest, lifePlan.user?.insurances, lifePlan.spouse?.insurances);
  // Insurance targets
  if (lifePlan.insurances) {
    updateReportsRequestWithInsuranceTargetsData(reportsRequest, lifePlan.insurances, config);
  }
  return reportsRequest;
}

function generateEmptyReportsRequest(): ReportsRequest {
  return {
    childs: [],
    childsEducationStudies: [],
    childsEducationSelectedProducts: [],
    deathProtectionSelectedProducts: [],
    debts: [],
    dependentPeople: [],
    employment: {},
    financialStatements: [],
    futureGoals: [],
    futureGoalsSelectedProducts: [],
    insurances: [],
    investments: [],
    partnerEmployment: {},
    partnerDeathProtectionSelectedProducts: [],
    partnerInvestments: [],
    partnerRetirementSelectedProducts: [],
    partnerSalaries: [],
    retirementSelectedProducts: [],
    salaries: []
  };
}

function updateReportsRequestWithUserPersonalData(reportsRequest: ReportsRequest, userPersonalData: PersonalData) {
  reportsRequest.self = {
    identifier: USER_IDENTIFIER,
    civilStatus: userPersonalData.civilStatus,
    gender: userPersonalData.gender,
    name: userPersonalData.name,
    relation: 'self',
    age: userPersonalData.birthDate ? getCurrentAge(userPersonalData.birthDate) : undefined
  };
  if (userPersonalData.birthDate) {
    reportsRequest.self.birthDate = formatISO(parseISO(userPersonalData.birthDate));
  }
  reportsRequest.childs = [];
  userPersonalData.children?.forEach(c => {
    reportsRequest.childs?.push({
      identifier: c.id,
      age: c.age,
      name: c.name,
      relation: 'child'
    });
  });
  reportsRequest.dependentPeople = [];
  userPersonalData.dependantPeople?.forEach(c => {
    reportsRequest.dependentPeople?.push({
      identifier: c.id,
      age: c.age,
      name: c.name,
      relation: 'other'
    });
  });
}

function updateReportsRequestWithSpousePersonalData(reportsRequest: ReportsRequest, spousePersonalData: PersonalData) {
  reportsRequest.partner = {
    identifier: SPOUSE_IDENTIFIER,
    birthDate: spousePersonalData.birthDate,
    civilStatus: spousePersonalData.civilStatus,
    gender: spousePersonalData.gender,
    name: spousePersonalData.name,
    relation: 'self',
    age: spousePersonalData.birthDate ? getCurrentAge(spousePersonalData.birthDate) : undefined
  };
  if (spousePersonalData.birthDate) {
    reportsRequest.partner.birthDate = formatISO(parseISO(spousePersonalData.birthDate));
  }
}

function updateReportsRequestWithUserProfessionalData(
  reportsRequest: ReportsRequest,
  userProfessionalData: ProfessionalData
) {
  reportsRequest.employment = getEmploymentFromProfessionalData(userProfessionalData);
}

function updateReportsRequestWithSpouseProfessionalData(
  reportsRequest: ReportsRequest,
  spouseProfessionalData: ProfessionalData
) {
  reportsRequest.partnerEmployment = getEmploymentFromProfessionalData(spouseProfessionalData);
}

function updateReportsRequestWithUserFinancialData(
  reportsRequest: ReportsRequest,
  userFinancialData: FinancialData,
  lifePlanInsurances: LifePlanInsurances,
  config: Config
) {
  reportsRequest.salaries = [];
  if (userFinancialData.netMonthlyIncome) {
    reportsRequest.salaries.push({
      netAmount: {
        value: userFinancialData.netMonthlyIncome.amount,
        currency: userFinancialData.netMonthlyIncome.currency
      },
      yearlyPayments: 12
    });
  }

  const financialStatements: FinancialStatement[] = [];
  if (userFinancialData.otherMonthlyIncome) {
    const otherMonthlyIncome = userFinancialData.otherMonthlyIncome;
    financialStatements.push({
      amount: {
        value: otherMonthlyIncome.amount,
        currency: otherMonthlyIncome.currency
      },
      filters: {
        concept: 'others',
        averagePeriodicity: 'monthly'
      },
      type: 'income'
    });
  }
  if (userFinancialData.generalMonthlyExpenditure) {
    const generalMonthlyExpenditure = userFinancialData.generalMonthlyExpenditure;
    financialStatements.push({
      amount: {
        value: generalMonthlyExpenditure.amount,
        currency: generalMonthlyExpenditure.currency
      },
      filters: {
        concept: 'others',
        averagePeriodicity: 'monthly'
      },
      type: 'expense'
    });
  }
  if (userFinancialData.detailedMonthlyExpenditure) {
    userFinancialData.detailedMonthlyExpenditure.forEach(detailedMonthlyExpenditure => {
      financialStatements.push({
        amount: {
          value: detailedMonthlyExpenditure.amount.amount,
          currency: detailedMonthlyExpenditure.amount.currency
        },
        filters: {
          concept: 'others', // TODO: not sure if this need to be: detailedMonthlyExpenditure.type
          averagePeriodicity: 'monthly'
        },
        type: 'expense'
      });
    });
  }
  reportsRequest.financialStatements = financialStatements;

  const investments: Investment[] = [];
  userFinancialData.savingsAndInvestments?.forEach((s, index) => {
    const investmentOption = config.sourceConfigurations?.investmentPurposeOptions.find(i => i.identifier === s.type);
    investments.push({
      identifier: INVESTMENT_IDENTIFIER_PREFIX + index,
      type: investmentOption?.params?.investmentType,
      currentValueAmount: {
        value: s.amount.amount,
        currency: s.amount.currency
      }
    });
  });
  reportsRequest.investments = investments;

  reportsRequest.investorProfile = userFinancialData.investorProfile;
  const futureGoals: FutureGoal[] = [];
  userFinancialData.futurePlans?.forEach(fp => {
    futureGoals.push({
      identifier: fp.id,
      currentAmount: {
        value: fp.assuredAmount.amount,
        currency: fp.assuredAmount.currency
      },
      goalAmount: {
        value: fp.goalAmount.amount,
        currency: fp.goalAmount.currency
      },
      endDate: formatISO(parseISO(fp.goalDate)),
      purpose: fp.type,
      type: 'all'
    });
  });
  // Create additional future goals for concrete types of SAVING insurances
  [ConcreteInsuranceIds.SAVING_GENERAL, ConcreteInsuranceIds.SAVING_INVESTMENT]
    .map(id => lifePlanInsurances[id])
    .filter(insuranceTarget => insuranceTarget)
    .forEach(insuranceTarget => {
      let purposeSuffix = '';
      switch (insuranceTarget.id) {
        case ConcreteInsuranceIds.SAVING_GENERAL:
          purposeSuffix = FUTURE_GOALS_PURPOSE_SAFETY_NET_SUFFIX;
          break;
        case ConcreteInsuranceIds.SAVING_INVESTMENT:
          purposeSuffix = FUTURE_GOALS_PURPOSE_INVESTMENTS_SUFFIX;
          break;
      }
      futureGoals.push({
        identifier: insuranceTarget.id,
        purpose: `futureGoals/${purposeSuffix}`,
        type: 'any'
      });
    });
  reportsRequest.futureGoals = futureGoals;

  const debts: Debt[] = [];
  userFinancialData.loans?.forEach(l => {
    const endDate = new Date(new Date().getFullYear() + l.remainingYears, 0, 1);
    debts.push({
      source: l.type,
      paymentsPeriodicAmount: {
        periodicity: 'monthly',
        amount: {
          value: l.monthlyFee.amount,
          currency: l.monthlyFee.currency
        }
      },
      pendingAmount: {
        value: l.pendingAmount.amount,
        currency: l.pendingAmount.currency
      },
      endDate: formatISO(endDate)
    });
  });
  reportsRequest.debts = debts;
}

function updateReportsRequestWithSpouseFinancialData(
  reportsRequest: ReportsRequest,
  spouseFinancialData: FinancialData
) {
  reportsRequest.partnerSalaries = [];
  if (spouseFinancialData.netMonthlyIncome) {
    reportsRequest.partnerSalaries.push({
      netAmount: {
        value: spouseFinancialData.netMonthlyIncome.amount,
        currency: spouseFinancialData.netMonthlyIncome.currency
      },
      yearlyPayments: 12
    });
  }
  reportsRequest.partnerInvestments = [];
}

const INSURANCES_TYPE_MAPPING: {
  [key in InsuranceType]: string;
} = {
  DEATH: 'DEATHS',
  EDUCATION: 'EDUCATION',
  SAVING: 'SAVING',
  RETIREMENT: 'RETIREMENT',
  DECUMULATION: 'DECUMULATION'
};

function updateReportsRequestWithInsurancesData(
  reportsRequest: ReportsRequest,
  userInsurancesData?: InsurancesData,
  spouseInsurancesData?: InsurancesData
) {
  const insurances: Insurance[] = [];
  if (reportsRequest.self && userInsurancesData) {
    const userPerson = reportsRequest.self;
    userInsurancesData.currentInsurances?.forEach(i => {
      const insurance: Insurance = {
        identifier: USER_IDENTIFIER + i.type,
        insuredPeople: [{ identifier: userPerson.identifier }],
        type: INSURANCES_TYPE_MAPPING[i.type],
        securedAmount: {
          value: i.assuredAmount.amount,
          currency: i.assuredAmount.currency
        }
      };
      if (i.monthlyFee && i.monthlyFee.amount) {
        insurance.paymentPeriodicAmount = {
          periodicity: 'monthly',
          amount: {
            value: i.monthlyFee.amount,
            currency: i.monthlyFee.currency
          }
        };
      }
      insurances.push(insurance);
    });
  }
  if (reportsRequest.partner && spouseInsurancesData) {
    const spousePerson = reportsRequest.partner;
    spouseInsurancesData.currentInsurances?.forEach(i => {
      const insurance: Insurance = {
        identifier: SPOUSE_IDENTIFIER + i.type,
        insuredPeople: [{ identifier: spousePerson.identifier }],
        type: INSURANCES_TYPE_MAPPING[i.type],
        securedAmount: {
          value: i.assuredAmount.amount,
          currency: i.assuredAmount.currency
        }
      };
      if (i.monthlyFee && i.monthlyFee.amount) {
        insurance.paymentPeriodicAmount = {
          periodicity: 'monthly',
          amount: {
            value: i.monthlyFee.amount,
            currency: i.monthlyFee.currency
          }
        };
      }
      insurances.push(insurance);
    });
  }
  reportsRequest.insurances = insurances;
}

function updateReportsRequestWithInsuranceTargetsData(
  reportsRequest: ReportsRequest,
  lifePlanInsurances: LifePlanInsurances,
  config: Config
) {
  const userPerson = reportsRequest.self;
  const spousePerson = reportsRequest.partner;
  // DEATH
  const deathUserInsurance = lifePlanInsurances[ConcreteInsuranceIds.DEATH_USER];
  reportsRequest.deathProtectionSelectedProducts = [];
  if (deathUserInsurance?.currentProduct?.cartStatus.added && userPerson) {
    reportsRequest.deathProtectionSelectedProducts.push({
      ...generateOfferingProductFromProduct(deathUserInsurance.currentProduct, config, deathUserInsurance.insured),
      person: { identifier: userPerson.identifier },
      purpose: 'death'
    });
  }
  const deathSpouseInsurance = lifePlanInsurances[ConcreteInsuranceIds.DEATH_SPOUSE];
  reportsRequest.partnerDeathProtectionSelectedProducts = [];
  if (deathSpouseInsurance?.currentProduct?.cartStatus.added && spousePerson) {
    reportsRequest.partnerDeathProtectionSelectedProducts.push({
      ...generateOfferingProductFromProduct(deathSpouseInsurance.currentProduct, config, deathSpouseInsurance.insured),
      person: { identifier: spousePerson.identifier },
      purpose: 'death'
    });
  }
  // EDUCATION
  const childsInsurances = Object.entries(lifePlanInsurances)
    .map(([id, insuranceTarget]) => insuranceTarget)
    .filter(insuranceTarget => insuranceTarget.insurance === 'EDUCATION' && insuranceTarget.currentProduct);
  const childsEducationSelectedProducts: OfferingProduct[] = [];
  const childsEducationStudies: EducationStudy[] = [];
  childsInsurances.forEach(insuranceTarget => {
    const childPerson = reportsRequest.childs?.find(c => c.identifier === insuranceTarget.id);
    if (childPerson) {
      if (insuranceTarget.currentProduct?.cartStatus.added) {
        childsEducationSelectedProducts.push({
          ...generateOfferingProductFromProduct(insuranceTarget.currentProduct, config, insuranceTarget.insured),
          person: { identifier: childPerson.identifier },
          purpose: 'education'
        });
      }
      if (insuranceTarget.insured.university) {
        childsEducationStudies.push({
          person: { identifier: childPerson.identifier },
          institution: insuranceTarget.insured.university,
          level: 'university'
        });
      }
    }
  });
  reportsRequest.childsEducationSelectedProducts = childsEducationSelectedProducts;
  reportsRequest.childsEducationStudies = childsEducationStudies;
  // SAVING
  const savingsInsurances = Object.entries(lifePlanInsurances)
    .map(([id, insuranceTarget]) => insuranceTarget)
    .filter(insuranceTarget => insuranceTarget.insurance === 'SAVING' && insuranceTarget.currentProduct);
  const futureGoalsSelectedProducts: OfferingProduct[] = [];
  savingsInsurances.forEach(insuranceTarget => {
    const futureGoal = reportsRequest.futureGoals?.find(fg => fg.identifier === insuranceTarget.id);
    if (futureGoal && insuranceTarget.currentProduct?.cartStatus.added && userPerson) {
      futureGoalsSelectedProducts.push({
        ...generateOfferingProductFromProduct(insuranceTarget.currentProduct, config, insuranceTarget.insured),
        person: { identifier: userPerson?.identifier },
        futureGoal: { identifier: futureGoal.identifier },
        purpose: futureGoal.purpose
      });
    }
  });
  reportsRequest.futureGoalsSelectedProducts = futureGoalsSelectedProducts;
  // RETIREMENT
  const retirementUserInsurance = lifePlanInsurances[ConcreteInsuranceIds.RETIREMENT_USER];
  reportsRequest.retirementSelectedProducts = [];
  if (retirementUserInsurance?.currentProduct?.cartStatus.added && userPerson) {
    reportsRequest.retirementSelectedProducts.push({
      ...generateOfferingProductFromProduct(
        retirementUserInsurance.currentProduct,
        config,
        retirementUserInsurance.insured
      ),
      person: { identifier: userPerson.identifier },
      purpose: 'retirement'
    });
  }
  const retirementSpouseInsurance = lifePlanInsurances[ConcreteInsuranceIds.RETIREMENT_SPOUSE];
  reportsRequest.partnerRetirementSelectedProducts = [];
  if (retirementSpouseInsurance?.currentProduct?.cartStatus.added && spousePerson) {
    reportsRequest.partnerRetirementSelectedProducts.push({
      ...generateOfferingProductFromProduct(
        retirementSpouseInsurance.currentProduct,
        config,
        retirementSpouseInsurance.insured
      ),
      person: { identifier: spousePerson.identifier },
      purpose: 'retirement'
    });
  }
}
